import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchBuilderDetail } from '@/api/questionnaires';
import type {
  BuilderDetail,
  BuilderMode,
  RawBuilderDetail,
} from '@/api/questionnaires/types';
import { normalizeBuilderDetail } from '../-utils/builderSerializers';

export const builderKeys = {
  all: ['questionnaires', 'builder'] as const,
  detail: (mode: BuilderMode, id: string) =>
    [...builderKeys.all, mode, id] as const,
};

/** The template endpoint returns `{ data: ... }`, the builder endpoint returns
 *  the detail at the top level. Unwrap so callers get a single shape. */
function unwrap(
  envelope: RawBuilderDetail | { data: RawBuilderDetail },
): RawBuilderDetail {
  return 'data' in envelope &&
    (envelope as { data: RawBuilderDetail }).data !== undefined
    ? (envelope as { data: RawBuilderDetail }).data
    : (envelope as RawBuilderDetail);
}

export function builderDetailQueryOptions(mode: BuilderMode, id: string) {
  return queryOptions({
    queryKey: builderKeys.detail(mode, id),
    queryFn: async (): Promise<BuilderDetail> => {
      const envelope = await fetchBuilderDetail(mode, id);
      return normalizeBuilderDetail(unwrap(envelope));
    },
  });
}

export function useBuilderDetailQuery(
  mode: BuilderMode,
  id: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...builderDetailQueryOptions(mode, id),
    enabled: options?.enabled ?? true,
  });
}
